import { useMemo } from 'react';
import type { FilterCondition, FilterExpression } from '../LookupField/types/filter.types';
import type { Fetcher, SearchRequest, SearchResponse } from '../LookupField/services/api.types';
import type { WizardOption } from './MasterDocument.types';

/**
 * `Fetcher` de LookupField servido DESDE MEMORIA, para los pasos del wizard de
 * creación cuya lista ya está cargada.
 *
 * ¿Por qué existe? Compañía y centro de operación se eligen en un `LookupField`
 * —el usuario necesita BUSCAR, no desplegar cien opciones— pero sus listas no son
 * una entidad consultable: las trae el adapter (`listCompanies` /
 * `listOperationCenters`) y llegan al wizard como `WizardOption[]` ya resueltas,
 * filtradas por permiso y por la compañía elegida. No hay endpoint `/search` al
 * que apuntar, y montar uno sería pedir dos veces lo mismo.
 *
 * `LookupField` sólo habla con su `fetcher`, así que basta con darle uno que
 * responda con el array que ya tenemos. La búsqueda, el orden y la paginación se
 * resuelven aquí con la misma semántica que espera del backend, de modo que el
 * componente no distingue un caso del otro.
 *
 * ⚠️ **Alcance deliberadamente corto.** Sólo implementa lo que LookupField llega a
 * pedir en este uso: búsqueda por `label` con los tres operadores y filtros de
 * IGUALDAD (los que manda al resolver el valor controlado por su `Id`). No
 * implementa `__contains`, `__in`, rangos ni `orderBy` por campo arbitrario,
 * porque el wizard no pasa `filters` ni `orderBy`. Si algún día se pasan, esto
 * los ignoraría en silencio — de ahí el aviso.
 */

/** Campo único de etiqueta: es el que se muestra y por el que se busca. */
export const WIZARD_LOOKUP_LABEL_FIELD = 'label';

/** Registro que ve LookupField. `Id` en PascalCase es su contrato. */
export interface WizardLookupRecord {
  Id: string;
  label: string;
  [key: string]: unknown;
}

/**
 * Texto comparable: minúsculas y SIN tildes. Sin plegar acentos, buscar "compa"
 * no encontraría "Compañía" en cuanto el usuario llega a la "ñ", que es justo el
 * caso de casi todos los nombres de compañía de Siesa.
 */
function plegar(valor: unknown): string {
  return String(valor ?? '')
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLocaleLowerCase();
}

/**
 * `startsWith` sobre una etiqueta COMPUESTA, que es lo que son estas: el adapter
 * las entrega como «código - nombre» («01 - SIESA SAS»).
 *
 * Un prefijo sobre la cadena completa sólo sirve para el código: teclear «SIESA»
 * no encontraría nada y el usuario concluiría que el buscador no funciona, cuando
 * el operador por defecto de LookupField es precisamente `startsWith` y no hay
 * prop para cambiarlo desde fuera. Así que aquí «empieza por» se evalúa también
 * contra cada PALABRA de la etiqueta.
 *
 * Es un superconjunto del prefijo estricto —todo lo que casaba antes sigue
 * casando—, así que no puede ocultar resultados; y `contains`, que el usuario
 * activa con el icono del buscador, se mantiene tal cual.
 */
function empiezaPorAlgunaPalabra(valor: string, texto: string): boolean {
  // Los separadores cubren las formas en que el adapter compone la etiqueta:
  // «01 - Principal», «01 — Centro A», «Bodega/Norte`».
  return valor.startsWith(texto)
    || valor.split(/[\s\-–—/·|,()]+/).some((palabra) => palabra.startsWith(texto));
}

function coincideBusqueda(
  registro: WizardLookupRecord,
  request: SearchRequest,
): boolean {
  const texto = plegar(request.search).trim();
  if (!texto) return true;
  const campos = request.searchFields?.length ? request.searchFields : [WIZARD_LOOKUP_LABEL_FIELD];
  return campos.some((campo) => {
    const valor = plegar(registro[campo]);
    switch (request.searchOperator) {
      // `equals` no es un modo que el usuario active: LookupField lo manda solo
      // cuando se teclea un código completo y se pulsa Enter sin haber navegado
      // por los resultados. Ahí sí es igualdad estricta contra la etiqueta entera.
      case 'equals': return valor === texto;
      case 'contains': return valor.includes(texto);
      // `startsWith` es el default del contrato cuando no viene operador.
      default: return empiezaPorAlgunaPalabra(valor, texto);
    }
  });
}

/** Igualdad laxa: los `Id` viajan como cadena y las opciones también. */
function igual(valorRegistro: unknown, valorFiltro: unknown): boolean {
  return String(valorRegistro ?? '') === String(valorFiltro ?? '');
}

/**
 * Filtros en formato expresivo: el array externo son AND, el interno OR
 * (`[[{a:1},{a:2}],[{b:3}]]` ⇒ `(a=1 OR a=2) AND b=3`), tal como los construye
 * LookupField para resolver el valor seleccionado.
 */
function pasaFiltros(registro: WizardLookupRecord, filters?: FilterExpression): boolean {
  if (!filters?.length) return true;
  return filters.every((grupo) =>
    !grupo?.length || grupo.some((condicion: FilterCondition) =>
      Object.entries(condicion).every(([campo, valor]) => igual(registro[campo], valor)),
    ),
  );
}

/** `WizardOption` → registro de LookupField. Sólo `id` y `label` intervienen. */
function aRegistro(option: WizardOption): WizardLookupRecord {
  return { Id: option.id, label: option.label };
}

/**
 * Construye el `Fetcher` que sirve `options` desde memoria.
 *
 * Conserva el ORDEN de entrada, que ya viene decidido por el adapter; ordenar
 * aquí alfabéticamente pisaría ese criterio (p. ej. la compañía por defecto
 * primero).
 */
export function crearFetcherDeOpciones(options: WizardOption[]): Fetcher {
  const registros = options.map(aRegistro);
  return (<TRecord,>(_entity: string, request: SearchRequest): Promise<SearchResponse<TRecord>> => {
    const coincidencias = registros
      .filter((r) => pasaFiltros(r, request.filters))
      .filter((r) => coincideBusqueda(r, request));
    // El paginado es lo que sostiene el scroll infinito de LookupField: decide que
    // hay más páginas cuando recibe exactamente `pageSize` registros, así que la
    // porción tiene que ser real y no el array completo.
    const desde = Math.max(0, (request.page - 1) * request.pageSize);
    const pagina = coincidencias.slice(desde, desde + request.pageSize);
    return Promise.resolve({
      // El contrato de `Fetcher` es genérico en el registro y aquí el tipo real es
      // `WizardLookupRecord`; LookupField sólo lee `Id` y los `displayFields`, que
      // es exactamente lo que se entrega.
      data: pagina as unknown as TRecord[],
      page: request.page,
      pageSize: request.pageSize,
    });
  }) as Fetcher;
}

/**
 * Igual que `crearFetcherDeOpciones`, pero con IDENTIDAD ESTABLE mientras el
 * contenido no cambie.
 *
 * Importa: `fetcher` está en las dependencias de los `useCallback` de carga de
 * LookupField, así que un fetcher nuevo en cada render invalida esos callbacks y
 * vuelve a disparar la carga. Y las listas llegan como props que el host
 * reconstruye a su ritmo, así que no se puede depender de la identidad del array:
 * la clave se deriva del CONTENIDO (`id` y `label` de cada opción), el mismo
 * recurso que usa LookupField con sus `displayFieldsKey`.
 */
export function useWizardOptionFetcher(options: WizardOption[]): Fetcher {
  const clave = options.map((o) => `${o.id}␟${o.label}`).join('␞');
  // `options` se lee a través de `clave`, que es lo que de verdad decide el
  // resultado; incluir el array reintroduciría la inestabilidad de identidad.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => crearFetcherDeOpciones(options), [clave]);
}
